import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    title: 'ヒアリング',
    subtitle: 'Consultation',
    desc: 'ご希望のスタイル・ご予算・日程などをお伺いします。ブライダルの場合はドレスやテーマカラーもご共有ください。',
    icon: 'ri-chat-smile-2-line',
  },
  {
    num: '02',
    title: 'デザイン提案',
    subtitle: 'Proposal',
    desc: 'ご要望をもとにデザインプランをご提案。花材のサンプルや参考画像をご覧いただきながら細部を決めていきます。',
    icon: 'ri-draft-line',
  },
  {
    num: '03',
    title: '花材の選定',
    subtitle: 'Sourcing',
    desc: '市場から最高品質の季節の花材を厳選。お客様のイメージに合う希少花材も国内外からお取り寄せいたします。',
    icon: 'ri-seedling-line',
  },
  {
    num: '04',
    title: '制作・お届け',
    subtitle: 'Creation & Delivery',
    desc: '当日の朝に制作し、最高の鮮度でお届け。結婚式場への搬入・設置も承ります。',
    icon: 'ri-gift-line',
  },
];

export default function ProcessSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 lg:py-36 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-noto text-xs tracking-[0.4em] text-[#c4956a] uppercase mb-4">How It Works</p>
          <h2 className="font-cormorant text-5xl lg:text-6xl text-[#2c2218] font-light mb-4">
            ご依頼の流れ
          </h2>
          <p className="font-noto text-sm text-[#6b5c4e]">
            初めての方でも安心してご利用いただけるよう、<br className="hidden md:block" />
            丁寧にサポートいたします。
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-[2.25rem] left-[12.5%] right-[12.5%] h-px bg-[#e8ddd0]"></div>

          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`relative text-center transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 150 + 200}ms` }}
            >
              {/* Number circle */}
              <div className="relative w-[4.5rem] h-[4.5rem] flex items-center justify-center bg-[#f0e8de] rounded-full mx-auto mb-6 z-10">
                <i className={`${step.icon} text-[#c4956a] text-2xl`}></i>
                <span className="absolute -top-2 -right-2 font-cormorant text-xs text-[#c4956a] bg-white rounded-full w-6 h-6 flex items-center justify-center border border-[#e8ddd0]">
                  {step.num}
                </span>
              </div>
              <h3 className="font-cormorant text-2xl text-[#2c2218] font-medium mb-1">{step.title}</h3>
              <p className="font-noto text-xs tracking-widest text-[#c4956a] mb-4">{step.subtitle}</p>
              <p className="font-noto text-xs text-[#6b5c4e] leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div
          className={`mt-20 relative rounded-2xl overflow-hidden transition-all duration-1000 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/144b9c421063a5d61e7d19aa31cb208a.jpg"
              alt="Wedding ceremony flowers"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-black/45"></div>
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 px-10 md:px-16 py-14">
            <div>
              <p className="font-noto text-xs tracking-[0.4em] text-[#c4956a] uppercase mb-3">Special Offer</p>
              <h3 className="font-cormorant text-4xl md:text-5xl text-white font-light leading-tight">
                ブライダルフラワー<br />
                <span className="italic">無料相談受付中</span>
              </h3>
            </div>
            <button
              onClick={() => {
                const el = document.getElementById('booking');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex-shrink-0 font-noto text-sm tracking-widest bg-white text-[#2c2218] px-10 py-4 rounded-full hover:bg-[#c4956a] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              無料相談を予約する
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
